import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ShieldCheck, Award, Ban, Calendar, CheckCircle2, XCircle, Printer } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isExpired = (d) => new Date(d) < new Date();

export default function PublicWarranty() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/warranties/${token}`)
      .then((r) => setDoc(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen grid place-items-center text-ink-mid text-sm">Loading…</div>;
  if (notFound || !doc) return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-2xl bg-[#FFE9E9] text-[#8B1E1E] grid place-items-center mx-auto mb-4"><ShieldCheck className="w-6 h-6" /></div>
        <div className="font-display text-2xl text-ink">Warranty not found</div>
        <div className="text-sm text-ink-mid mt-2">Please contact your clinic if you believe this is an error.</div>
      </div>
    </div>
  );

  const expired = isExpired(doc.valid_till);
  const active = !doc.voided && !expired;
  // Derive warranty status once; keeps JSX free of nested ternaries.
  const statusKind = doc.voided ? "voided" : expired ? "expired" : "active";
  const STATUS_STYLES = {
    voided:  { color: "#8B1E1E", bg: "#FFE9E9", Icon: Ban,           label: "Voided"  },
    expired: { color: "#8A6C1F", bg: "#FFF6D9", Icon: XCircle,       label: "Expired" },
    active:  { color: "#2D6A4F", bg: "#E8F0EB", Icon: CheckCircle2,  label: "Active"  },
  };
  const { color: statusColor, bg: statusBg, Icon: StatusIcon, label: statusLabel } = STATUS_STYLES[statusKind];

  return (
    <div className="min-h-screen bg-linen/30 py-8 px-4">
      <Toaster />
      <div className="max-w-lg mx-auto">
        {/* Warranty card */}
        <div className="relative bg-gradient-to-br from-pine to-[#2A5442] text-linen rounded-3xl p-6 shadow-lg" data-testid="warranty-card-public">
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold"
               style={{ background: statusBg, color: statusColor }}>
            <StatusIcon className="w-3 h-3" /> {statusLabel}
          </div>
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-linen/15 grid place-items-center flex-shrink-0"><Award className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="overline opacity-70">Warranty card</div>
              <div className="font-display text-lg leading-tight">{doc.clinic_name}</div>
            </div>
          </div>
          <div className="mt-6">
            <div className="text-xs opacity-70">Warranty #</div>
            <div className="font-mono text-lg tracking-widest">{doc.warranty_number}</div>
          </div>
          <div className="mt-4">
            <div className="text-xs opacity-70">Procedure</div>
            <div className="font-display text-xl leading-tight">
              {doc.procedure}
              {doc.variant && <span className="opacity-80"> — {doc.variant}</span>}
            </div>
            {doc.tooth && <div className="text-xs opacity-70 mt-1">Tooth #{doc.tooth}</div>}
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6 border-t border-linen/20 pt-4">
            <div>
              <div className="text-[10px] uppercase opacity-70 tracking-widest">Issue date</div>
              <div className="text-sm font-mono mt-0.5 inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {doc.issue_date}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase opacity-70 tracking-widest">Valid till</div>
              <div className={`text-sm font-mono mt-0.5 inline-flex items-center gap-1.5 ${active ? "" : "opacity-70"}`}>
                <Calendar className="w-3.5 h-3.5" /> {doc.valid_till}
              </div>
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase opacity-70 tracking-widest">Duration</div>
            <div className="text-sm mt-0.5">{doc.duration_years} year{doc.duration_years !== 1 ? "s" : ""} {doc.duration_months > 0 ? `${doc.duration_months} months` : ""}</div>
          </div>
          <div className="mt-6 border-t border-linen/20 pt-3">
            <div className="text-xs opacity-70">Issued to</div>
            <div className="text-sm">{doc.patient_name}</div>
            <div className="text-xs opacity-70 mt-2">By</div>
            <div className="text-sm">{doc.dentist_name}</div>
          </div>
        </div>

        {/* Voided reason */}
        {doc.voided && (
          <div className="mt-3 rounded-2xl border border-[#F0A5A5] bg-[#FFF3F3] p-4">
            <div className="overline text-[#8B1E1E]">Voided</div>
            <div className="text-sm text-[#8B1E1E] mt-1">{doc.voided_reason}</div>
          </div>
        )}

        {/* Coverage + exclusions */}
        <div className="mt-4 bg-white rounded-3xl p-6 border border-[#E8EAE9] space-y-5">
          <div>
            <div className="overline text-ink-soft">Coverage</div>
            <ul className="list-disc pl-5 text-sm text-ink/80 mt-2 space-y-1">
              {doc.coverage.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-[#E5B34B] bg-[#FFF8E7] p-4">
            <div className="overline text-[#B0521F]">Exclusions — warranty is void if</div>
            <ul className="list-disc pl-5 text-sm text-ink/80 mt-2 space-y-1">
              {doc.exclusions.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
          {doc.notes && (
            <div className="rounded-2xl bg-linen/50 p-3 border border-[#E8EAE9]">
              <div className="overline text-ink-soft">Notes</div>
              <div className="text-sm text-ink/80 mt-1">{doc.notes}</div>
            </div>
          )}
        </div>

        <button data-testid="wty-print-btn" onClick={() => window.print()}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium bg-pine text-linen hover:bg-pine-mid transition-all">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
        <div className="text-[10px] text-ink-soft text-center mt-4">This is a digitally-issued warranty. Present this card at the clinic for any claims within the validity period.</div>
      </div>
    </div>
  );
}
